import { AuditLogModalComponent } from '@administration/audit-logs/modals/audit-log-modal/audit-log-modal.component';
import { AddDocumentModalComponent } from '@administration/help/modals/add-document-modal/add-document-modal.component';
import { AddFaqModalComponent } from '@administration/help/modals/add-faq-modal/add-faq-modal.component';
import { MessageModalComponent } from '@administration/message/modals/message-modal.component';
import { Injectable, Type } from '@angular/core';
import { marker } from '@common/translate-marker';
import { AttachmentListDialogComponent } from '@common/components/attachment-list-dialog/attachment-list-dialog.component';
import { ChangesDialogComponent } from '@common/components/changes-dialog.component';
import { ConfirmTwoOptionsDialogComponent } from '@common/components/confirm-two-options-dialog';
import { FileUploadGenericModalComponent } from '@common/components/file-upload-generic-modal/file-upload-generic-modal-component';
import type { TabNotificationsComponent } from '@common/components/tab-notifications/tab-notifications.component';
import { ErrorDialogComponent } from '@common/error-dialog.component';
import { ServiceRequestItemModalTypes } from '@common/knownTypes/ServiceRequestItemModalTypes';
import type { AddContainersFromTosComponent } from '@common/modals/add-containers-from-tos/add-containers-from-tos.component';
import { CompleteCancelModalComponent } from '@common/modals/complete-cancel-modal/complete-cancel-modal.component';
import { SaveCargoItemComponent } from '@common/modals/save-cargo-item/save-cargo-item.component';
import { XmlImportConfirmationComponent } from '@common/modals/xml-import-confirmation/xml-import-confirmation.component';
import { XmlImportReportComponent } from '@common/modals/xml-import-report/xml-import-report.component';
import {
    ValidationOutcomeRow,
    ValidationOutcomeRowLike,
    ValidationOutcomesOptions
} from '@common/models/validation-outcomes.model';
import { ViewMode } from '@common/models/view-mode';
import { isMobile } from '@common/utils/isMobile';
import { TranslateService } from '@ngx-translate/core';
import { DialogRef, DialogService } from '@progress/kendo-angular-dialog';
import { CustomsDeclarationModalComponent } from 'app/customs/modals/customs-declaration-modal/customs-declaration-modal.component';
import type { ServiceRequestsDeclarationTabComponent } from 'app/customs/tabs/service-requests-declaration-tab/service-requests-declaration-tab.component';
import { EditReleaseOrderModalModel } from 'app/tos-operations/models/EditReleaseOrderModal.model';
import { ServiceRequestDisplayModel } from 'app/tos-operations/models/ServiceRequest.display.model';
import { ServiceRequestMotLinkItem } from 'app/tos-operations/models/ServiceRequestLinkItemsToMotsModal.model';
import type { AddShipmentUnitsFromBulPCSModalComponent } from 'app/tos-operations/service-request/modals/add-shipment-units-from-bulpcs-modal/add-shipment-units-from-bulpcs-modal.component';
import type { DocumentsForServiceRequestModalComponent } from 'app/tos-operations/service-request/modals/documents-for-service-request-modal/documents-for-service-request-modal.component';
import { ValidationModalComponent } from 'app/tos-operations/service-request/modals/validation-modal/validation-modal.component';
import { CreateCustomInspectionType } from 'app/tos-operations/service-request/models/CreateCustomInspectionType';
import { CustomsProperties } from 'app/tos-operations/service-request/models/CusomsPropertiesModel';
import { AddDeliveryOrderItemComponent } from 'app/vessel-visit/delivery-order-list/modals/add-delivery-order-item/add-delivery-order-item.component';
import type { DeliveryOrderItemQuantitiesModalComponent } from 'app/vessel-visit/delivery-order-list/modals/delivery-order-item-quantities-modal/delivery-order-item-quantities-modal.component';
import { EditDeliveryOrderComponent } from 'app/vessel-visit/delivery-order-list/modals/edit-delivery-order/edit-delivery-order.component';
import { GeneralActManualCorrectionModalComponent } from 'app/vessel-visit/general-act/modals/general-act-manual-correction-modal/general-act-manual-correction-modal.component';
import { SaveGeneralActModalComponent } from 'app/vessel-visit/general-act/modals/save-general-act-modal/save-general-act-modal.component';
import { GeneralActDisplayModel } from 'app/vessel-visit/general-act/models/GeneralAct.display.model';
import type { VesselVisitServiceRequestsModalComponent } from 'app/vessel-visit/modals/vessel-visit-service-requests-modal/vessel-visit-service-requests-modal.component';
import { ManifestVehicleEditModalComponent } from 'app/vessel-visit/vessel-visit-list/components/modals/manifest-vehicle-edit-modal/manifest-vehicle-edit-modal.component';
import { CreateServiceRequestFromCargoManifestModalComponent } from 'app/vessel-visit/vessel-visit-list/components/vessel-visit-edit/create-service-request-from-cargo-manifest-modal/create-service-request-from-cargo-manifest-modal.component';
import { DuplicateCmiWarningModalComponent } from 'app/vessel-visit/vessel-visit-list/components/vessel-visit-edit/duplicate-cmi-warning-modal/duplicate-cmi-warning-modal.component';
import { SaveVehicleModalComponent } from 'app/vessel-visit/vessel-visit-list/components/vessel-visit-edit/modals/save-vehicle-modal/save-vehicle-modal.component';
import { AddVesselComponent } from 'app/vessel/modals/add-vessel/add-vessel.component';
import { firstValueFrom, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ConfirmDialogComponent } from '../components/confirm-dialog';
import { XmlTypes } from '../knownTypes/XmlTypes';

/**
 * Kendo dialog does not have s/lg/xl modal sizes like bootstrap, width and height need to be specified
 */
export enum ModalWidth {
    sm = '30%',
    md = '40%',
    lg = '50%',
    xl = '70%',
    xxl = '95%'
}
@Injectable({
    providedIn: 'root'
})
export class ModalService {
    isMobile: boolean;
    constructor(
        private modalService: DialogService,
        private translateService: TranslateService
    ) {
        this.isMobile = isMobile();
    }

    public confirm(
        title: string,
        message: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: ConfirmDialogComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.sm
        });
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return dialogRef.result;
    }

    public confirmTwoOptions(
        title: string,
        message: string,
        firstButtonName?: string,
        secondButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: ConfirmTwoOptionsDialogComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.sm
        });

        dialogRef.content.instance.title = title;
        dialogRef.content.instance.message = message;
        dialogRef.content.instance.firstButtonName = firstButtonName;
        dialogRef.content.instance.secondButtonName = secondButtonName;
        dialogRef.content.instance.cancelButtonName = cancelButtonName;

        return dialogRef.result;
    }

    public changes(title: string, message: string): Observable<any> {
        const dialogRef = this.modalService.open({ content: ChangesDialogComponent });
        dialogRef.content.instance.title = title;
        dialogRef.content.instance.message = message;

        return dialogRef.result;
    }

    public addNominatedAgent(
        modalComponent: any,
        vesselVisitId: number,
        agentsCurrentlyInTab: any[],
        title: string,
        message: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.xl
        });
        dialogRef.content.instance.agentsCurrentlyInTab = agentsCurrentlyInTab;
        dialogRef.content.instance.vesselVisitId = vesselVisitId;

        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return dialogRef.result;
    }

    public saveCargoItem(
        manifestId,
        containerized,
        forTosSystem,
        isArrival,
        cargoManifestItem?: any,
        cargoManifestMswDGContact?: string,
        cargoManifestTypeId?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({ content: SaveCargoItemComponent, width: ModalWidth.xxl });
        dialogRef.content.instance.containerized = containerized;
        dialogRef.content.instance.manifestId = manifestId;
        dialogRef.content.instance.isArrival = isArrival;
        dialogRef.content.instance.forTosSystem = forTosSystem;
        dialogRef.content.instance.cargoManifestItem = cargoManifestItem;
        dialogRef.content.instance.cargoManifestMswDGContact = cargoManifestMswDGContact;
        dialogRef.content.instance.cargoManifestTypeId = cargoManifestTypeId;
        // dialogRef.content.instance.codeLists = codeLists;

        return dialogRef.result;
    }

    public addTosToNominatedAgent(
        modalComponent: any,
        nominatedAgentId: number,
        title: string,
        message: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        dialogRef.content.instance.nominatedAgentId = nominatedAgentId;

        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return dialogRef.result;
    }

    public addTosVoyageNumber(
        modalComponent: any,
        nominatedAgentId: number,
        title: string,
        message: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        dialogRef.content.instance.nominatedAgentId = nominatedAgentId;

        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return dialogRef.result;
    }

    public navisLoginModal(
        modalComponent: any,
        title: string,
        message: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });

        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return dialogRef.result;
    }

    public changeNavisPasswordModal(
        modalComponent: any,
        title: string,
        message: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });

        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return dialogRef.result;
    }

    public CompareChangesCargoListModal(
        modalComponent: any,
        unmatchingCargoList: any[],
        title: string,
        message: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Promise<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        dialogRef.content.instance.unmatchingCargoList = unmatchingCargoList;

        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return firstValueFrom(dialogRef.result);
    }

    public cargoListContainerSumModal(
        modalComponent: any,
        agentType: string,
        agentTypeId: number,
        title: string,
        message: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Promise<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        dialogRef.content.instance.agentType = agentType;
        dialogRef.content.instance.agentTypeId = agentTypeId;

        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return firstValueFrom(dialogRef.result);
    }

    public approveOrderedInsertCustomsCtrNbr(
        modalComponent: any,
        title: string,
        message: string,
        serviceRequestId?: number,
        id?: number,
        type?: any,
        srSum?: any,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        dialogRef.content.instance.serviceRequestId = serviceRequestId;
        dialogRef.content.instance.id = id;
        dialogRef.content.instance.type = type;
        dialogRef.content.instance.srSum = srSum;

        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return dialogRef.result;
    }

    public addServiceRequestMotItem(
        modalComponent: any,
        title: string,
        message: string,
        serviceRequestId: number,
        lastSequenceNumber: number,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        dialogRef.content.instance.serviceRequestId = serviceRequestId;
        dialogRef.content.instance.lastSequenceNumber = lastSequenceNumber;

        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return dialogRef.result;
    }

    public addLandServiceRequest(
        modalComponent: any,
        title: string,
        message: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);
        return dialogRef.result;
    }

    public createServiceRequestFromShipmentPool(
        modalComponent: any,
        title: string,
        context: { port: string; terminal: string; terminalOperator: number; tosSystem: string },
        shipmentPoolItemIds: number[],
        shipmentPoolIds: number[],
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        dialogRef.content.instance.fromShipmentPool = true;
        dialogRef.content.instance.shipmentPoolContext = context;
        dialogRef.content.instance.shipmentPoolItemIds = shipmentPoolItemIds ?? [];
        dialogRef.content.instance.shipmentPoolIds = shipmentPoolIds ?? [];
        this.setCommonProperties(dialogRef, title, null, yesButtonName, noButtonName, cancelButtonName);
        return dialogRef.result;
    }

    public createServiceRequestFromReleaseOrder(
        modalComponent: any,
        title: string,
        context: any,
        releaseOrderId: number,
        releaseOrderItemIds: number[],
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        dialogRef.content.instance.fromReleaseOrder = true;
        dialogRef.content.instance.releaseOrderContext = context;
        dialogRef.content.instance.releaseOrderId = releaseOrderId;
        dialogRef.content.instance.releaseOrderItemIds = releaseOrderItemIds ?? [];
        this.setCommonProperties(dialogRef, title, null, yesButtonName, noButtonName, cancelButtonName);
        return dialogRef.result;
    }

    public sendServiceRequestToTerminal(
        modalComponent: any,
        title: string,
        message: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string,
        confirmationText?: string,
        serviceRequestId?: any,
        errors?: string[],
        warnings?: string[]
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);
        dialogRef.content.instance.serviceRequestId = serviceRequestId;
        dialogRef.content.instance.errors = errors;
        dialogRef.content.instance.warnings = warnings;
        dialogRef.content.instance.confirmationText = confirmationText;
        return dialogRef.result;
    }

    public approveServiceRequest(
        modalComponent: any,
        title: string,
        message: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string,
        serviceRequestId?: any,
        requestedStartDateTime?: any,
        version?: number,
        willApproveSRCreateAmendmentDocuments?: boolean
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);
        dialogRef.content.instance.serviceRequestId = serviceRequestId;
        dialogRef.content.instance.requestedStartDateTime = requestedStartDateTime;
        dialogRef.content.instance.version = version;
        dialogRef.content.instance.willApproveSRCreateAmendmentDocuments = willApproveSRCreateAmendmentDocuments;
        return dialogRef.result;
    }

    public completeServiceRequest(
        modalComponent: any,
        title: string,
        message: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string,
        serviceRequestId?: any,
        allowCompletingFromAPTStatus?: boolean,
        showSummary?: boolean,
        showSummaryRow?: boolean,
        isRoro?: boolean
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);
        dialogRef.content.instance.serviceRequestId = serviceRequestId;
        dialogRef.content.instance.allowCompletingFromAPTStatus = allowCompletingFromAPTStatus;
        dialogRef.content.instance.showSummary = showSummary;
        dialogRef.content.instance.showSummaryRow = showSummaryRow;
        dialogRef.content.instance.isRoro = isRoro;
        return dialogRef.result;
    }

    public openValidationOutcomes(options: ValidationOutcomesOptions): Observable<any> {
        const dialogRef = this.modalService.open({
            content: ValidationModalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        const instance = dialogRef.content.instance as ValidationModalComponent;

        this.setCommonProperties(dialogRef, options.title, options.message);
        instance.closeButtonName = options.closeButtonName || this.translateService.instant(marker('Ok'));
        instance.rows = this.normalizeValidationRows(options.rows);
        instance.shipmentTypeId = options.shipmentTypeId;
        instance.forExcel = options.forExcel;
        instance.itemHeader = options.itemHeader;
        instance.showSubtitle = options.showSubtitle;
        instance.firstColumnHeader = options.firstColumnHeader;
        instance.secondColumnHeader = options.secondColumnHeader;
        instance.emptyStateText = options.emptyStateText;
        instance.summary = options.summary;

        if (options.rowsLoader) {
            instance.isBusy = true;
            options.rowsLoader.pipe(takeUntil(instance.destroyed$)).subscribe({
                next: (rows) => {
                    instance.rows = this.normalizeValidationRows(rows);
                    instance.isBusy = false;
                },
                error: () => {
                    instance.rows = [];
                    instance.isBusy = false;
                }
            });
        }

        return dialogRef.result;
    }

    public uploadExcelOnNominatedAgentResult(
        modalComponent: any,
        title: string,
        message: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string,
        errors?: any[]
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);
        dialogRef.content.instance.errors = errors;
        return dialogRef.result;
    }

    public declineServiceRequest(
        modalComponent: any,
        title: string,
        message: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string,
        serviceRequestId?: any
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);
        dialogRef.content.instance.serviceRequestId = serviceRequestId;
        return dialogRef.result;
    }

    public cancelServiceRequest(
        modalComponent: any,
        title: string,
        message: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string,
        serviceRequestId?: any
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);
        dialogRef.content.instance.serviceRequestId = serviceRequestId;
        return dialogRef.result;
    }

    public holdServiceRequestItems(
        modalComponent: any,
        title: string,
        message: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string,
        serviceRequestVersionId?: number,
        serviceRequestItemIds?: any[],
        shipmentItemIds?: any[],
        containerItemIds?: any[],
        containerVisitIds?: any[],
        shipmentPoolIds?: any[],
        shipmentPoolItemIds?: any[],
        isUnholdAction?: boolean,
        shipmentType?: string,
        activeHoldTypeIds?: any[],
        isUnholdRoleIdAvailable?: boolean,
        serviceRequestIds?: any[]
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);
        dialogRef.content.instance.serviceRequestVersionId = serviceRequestVersionId;
        dialogRef.content.instance.serviceRequestItemIds = serviceRequestItemIds;
        dialogRef.content.instance.shipmentItemIds = shipmentItemIds;
        dialogRef.content.instance.containerItemIds = containerItemIds;
        dialogRef.content.instance.containerVisitIds = containerVisitIds;
        dialogRef.content.instance.shipmentPoolIds = shipmentPoolIds;
        dialogRef.content.instance.shipmentPoolItemIds = shipmentPoolItemIds;
        dialogRef.content.instance.isUnholdAction = isUnholdAction;
        dialogRef.content.instance.shipmentType = shipmentType;
        dialogRef.content.instance.activeHoldTypeIds = activeHoldTypeIds;
        dialogRef.content.instance.isUnholdRoleIdAvailable = isUnholdRoleIdAvailable;
        dialogRef.content.instance.serviceRequestIds = serviceRequestIds;
        return dialogRef.result;
    }

    public openHoldDetailsModal(
        modalComponent: any,
        title: string,
        message: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string,
        id?: number,
        entity?: string,
        serviceRequestId?: number,
        containerVisitId?: number,
        serviceRequestVersionShipmentId?: number,
        serviceRequestVersionContainerId?: number,
        serviceRequestVersionShipmentDetailId?: number,
        shipmentPoolId?: number,
        shipmentPoolItemId?: number
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);
        dialogRef.content.instance.id = id;
        dialogRef.content.instance.entity = entity;
        dialogRef.content.instance.serviceRequestId = serviceRequestId;
        dialogRef.content.instance.containerVisitId = containerVisitId;
        dialogRef.content.instance.serviceRequestVersionShipmentId = serviceRequestVersionShipmentId;
        dialogRef.content.instance.serviceRequestVersionContainerId = serviceRequestVersionContainerId;
        dialogRef.content.instance.serviceRequestVersionShipmentDetailId = serviceRequestVersionShipmentDetailId;
        dialogRef.content.instance.shipmentPoolId = shipmentPoolId;
        dialogRef.content.instance.shipmentPoolItemId = shipmentPoolItemId;
        return dialogRef.result;
    }

    public confirmMoveServiceRequestItems(
        modalComponent: any,
        title: string,
        message: string,
        containerItemIds: any[],
        serviceRequestId: number,
        manipulation: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string,
        isUnnamedContainer?: boolean
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.xl
        });
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);
        dialogRef.content.instance.containerItemIds = containerItemIds;
        dialogRef.content.instance.serviceRequestId = serviceRequestId;
        dialogRef.content.instance.manipulation = manipulation;
        dialogRef.content.instance.isUnnamedContainer = isUnnamedContainer;
        return dialogRef.result;
    }

    public cancelMoveServiceRequestItems(
        modalComponent: any,
        title: string,
        message: string,
        containerItemIds: any[],
        serviceRequestId: number,
        manipulation: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.sm
        });
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);
        dialogRef.content.instance.containerItemIds = containerItemIds;
        dialogRef.content.instance.serviceRequestId = serviceRequestId;
        dialogRef.content.instance.manipulation = manipulation;
        return dialogRef.result;
    }

    public createCustomInspection(
        modalComponent: any,
        title: string,
        message: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string,
        items?: any[],
        createCustomInspectionType?: CreateCustomInspectionType,
        serviceRequestId?: number
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);
        dialogRef.content.instance.items = items;
        dialogRef.content.instance.createCustomInspectionType = createCustomInspectionType;
        dialogRef.content.instance.serviceRequestId = serviceRequestId;
        return dialogRef.result;
    }

    public cancelStorageDocument(
        modalComponent: any,
        title: string,
        message: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string,
        storageDocumentId?: any
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);
        dialogRef.content.instance.storageDocumentId = storageDocumentId;
        return dialogRef.result;
    }

    public cancelReleaseOrder(
        modalComponent: any,
        title: string,
        message: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string,
        releaseOrderId?: any
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);
        dialogRef.content.instance.releaseOrderId = releaseOrderId;
        return dialogRef.result;
    }

    public cancelAmendmentDocument(
        modalComponent: any,
        title: string,
        message: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string,
        amendmentDocumentId?: any
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);
        dialogRef.content.instance.amendmentDocumentId = amendmentDocumentId;
        return dialogRef.result;
    }

    public editCargoDamageRemark(
        modalComponent: any,
        title: string,
        message: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string,
        remark?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);
        dialogRef.content.instance.remark = remark;
        return dialogRef.result;
    }

    public userActivate(
        modalComponent: any,
        userId: number,
        title: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.xl
        });
        dialogRef.content.instance.userId = userId;
        this.setCommonProperties(dialogRef, title, '', yesButtonName, noButtonName, cancelButtonName);
        return dialogRef.result;
    }

    public addLandServiceRequestRelation(
        modalComponent: any,
        title: string,
        srManipulation: string,
        fullContainers: boolean,
        forTOSSystem: any,
        serviceRequestType: string,
        serviceRequestId: number,
        message: string,
        relationData?: any,
        mode?: ServiceRequestItemModalTypes,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.xl
        });
        dialogRef.content.instance.srManipulation = srManipulation;
        dialogRef.content.instance.fullContainers = fullContainers;
        dialogRef.content.instance.forTOSSystem = forTOSSystem;
        dialogRef.content.instance.serviceRequestType = serviceRequestType;
        dialogRef.content.instance.serviceRequestId = serviceRequestId;
        dialogRef.content.instance.dataForEditing = relationData;
        dialogRef.content.instance.mode = mode;
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);
        return dialogRef.result;
    }

    public addOnExistingCargoListFromContainerVisitModal(
        modalComponent: any,
        agentType: string,
        agentTypeId: number,
        full: boolean,
        cargoDirection: string,
        tosServiceRequestNumber: string,
        serviceRequestId: number,
        title: string,
        message: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Promise<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.xl
        });
        dialogRef.content.instance.agentType = agentType;
        dialogRef.content.instance.agentTypeId = agentTypeId;
        dialogRef.content.instance.full = full;
        dialogRef.content.instance.cargoDirection = cargoDirection;
        dialogRef.content.instance.tosServiceRequestNumber = tosServiceRequestNumber;
        dialogRef.content.instance.serviceRequestId = serviceRequestId;

        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return firstValueFrom(dialogRef.result);
    }

    public removeContainerFromCLAndSrModal(
        modalComponent: any,
        cargoDirection: string,
        tosServiceRequestNumber: string,
        cargoListGridView: any,
        serviceRequestId: number,
        isAdmin: boolean,
        title: string,
        message: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Promise<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        dialogRef.content.instance.cargoDirection = cargoDirection;
        dialogRef.content.instance.tosServiceRequestNumber = tosServiceRequestNumber;
        dialogRef.content.instance.cargoListGridView = cargoListGridView;
        dialogRef.content.instance.serviceRequestId = serviceRequestId;
        dialogRef.content.instance.isAdmin = isAdmin;

        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return firstValueFrom(dialogRef.result);
    }

    public updateAppointmentModal(
        modalComponent: any,
        serviceRequest: ServiceRequestDisplayModel,
        serviceRequestRelation: any,
        title: string,
        message: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.xl
        });

        dialogRef.content.instance.serviceRequest = serviceRequest;
        dialogRef.content.instance.serviceRequestRelation = serviceRequestRelation;
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return dialogRef.result;
    }

    public editVesselServiceRequestRelation(
        modalComponent: any,
        title: string,
        forTOSSystem: any,
        serviceRequestType: string,
        serviceRequestId: number,
        message: string,
        dataForEditing?: any,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.xl
        });
        dialogRef.content.instance.forTOSSystem = forTOSSystem;
        dialogRef.content.instance.serviceRequestType = serviceRequestType;
        dialogRef.content.instance.serviceRequestId = serviceRequestId;
        dialogRef.content.instance.dataForEditing = dataForEditing;
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);
        return dialogRef.result;
    }

    public updateServiceOrderModal(
        modalComponent: any,
        serviceRequest: ServiceRequestDisplayModel,
        title: string,
        message: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });

        dialogRef.content.instance.serviceRequest = serviceRequest;
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return dialogRef.result;
    }

    public addManifestAttachmentModal(
        modalComponent: any,
        manifest: string,
        title: string,
        message: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });

        dialogRef.content.instance.manifest = manifest;
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return dialogRef.result;
    }

    public editCustomsClearingOnTerminalData(
        modalComponent: any,
        serviceRequest: ServiceRequestDisplayModel,
        title: string,
        message: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.xl
        });

        dialogRef.content.instance.serviceRequest = serviceRequest;
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return dialogRef.result;
    }

    public customsControlNumberOnManifestModal(
        modalComponent: any,
        customsControlNbrType: string,
        customsControlNumber: string,
        title: string,
        message: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });

        dialogRef.content.instance.customsControlNbrType = customsControlNbrType;
        dialogRef.content.instance.customsControlNumber = customsControlNumber;
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return dialogRef.result;
    }

    public changeLanguage(
        currentLanguage: any,
        modalComponent: any,
        title: string,
        message: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.sm
        });

        dialogRef.content.instance.currentLanguage = currentLanguage;
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return dialogRef.result;
    }

    public containerBlockUnblock(
        modalComponent: any,
        title: string,
        message: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });

        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return dialogRef.result;
    }

    public updateMrn(
        modalComponent: any,
        title: string,
        message: string,
        currentMrn: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        dialogRef.content.instance.currentMrn = currentMrn;
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return dialogRef.result;
    }

    public updateCustomsStatus(
        modalComponent: any,
        title: string,
        message: string,
        currentCustomsStatus: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        dialogRef.content.instance.currentCustomsStatus = currentCustomsStatus;
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return dialogRef.result;
    }

    public openNavisConsigneeListModal(
        modalComponent: any,
        title: string,
        message: string,
        navisUser: any
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        dialogRef.content.instance.navisUser = navisUser;

        this.setCommonProperties(dialogRef, title, message);

        return dialogRef.result;
    }

    public openSrlistModalForDeclaration(
        component: Type<ServiceRequestsDeclarationTabComponent>,
        mrnId: number,
        mrn: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: component,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.xl
        });
        dialogRef.content.instance.mrnId = mrnId;
        dialogRef.content.instance.mrn = mrn;
        dialogRef.content.instance.isModal = true;
        this.setCommonProperties(
            dialogRef,
            this.translateService.instant(marker('SR List')),
            '',
            this.translateService.instant(marker('Add')),
            null,
            this.translateService.instant(marker('Cancel'))
        );
        return dialogRef.result;
    }

    public openSrlistModalForVesselVisit(
        component: Type<VesselVisitServiceRequestsModalComponent>,
        vesselVisitId: number,
        cargoManifestId?: number
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: component,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.xl
        });
        dialogRef.content.instance.vesselVisitId = vesselVisitId;
        dialogRef.content.instance.cargoManifestId = cargoManifestId;
        this.setCommonProperties(
            dialogRef,
            this.translateService.instant(marker('SR List')),
            '',
            this.translateService.instant(marker('Add')),
            null,
            this.translateService.instant(marker('Cancel'))
        );
        return dialogRef.result;
    }

    public approveRealizedTrainServiceRequest(
        modalComponent: any,
        title: string,
        message: string,
        srSum?: any,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        dialogRef.content.instance.srSum = srSum;
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return dialogRef.result;
    }

    public openMotOnServiceRequestModal(
        modalComponent: any,
        dataForEditing: any,
        mode: string,
        title: string,
        message: string,
        motType: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        dialogRef.content.instance.dataForEditing = dataForEditing;
        dialogRef.content.instance.mode = mode;
        dialogRef.content.instance.motType = motType;
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return dialogRef.result;
    }

    public openBulkUpdateMotIdentifiersModal(
        modalComponent: any,
        selectedCount: number,
        motType: string,
        title: string,
        message: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        dialogRef.content.instance.selectedCount = selectedCount;
        dialogRef.content.instance.motType = motType;
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return dialogRef.result;
    }

    public openMotOnStorageDocumentModal(
        modalComponent: any,
        dataForEditing: any,
        mode: string,
        title: string,
        message: string,
        motType: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        dialogRef.content.instance.dataForEditing = dataForEditing;
        dialogRef.content.instance.mode = mode;
        dialogRef.content.instance.motType = motType;
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return dialogRef.result;
    }

    public selectShipmentPoolModal(
        modalComponent: any,
        title: string,
        message: string,
        serviceRequestId: number,
        manipulation: string,
        shipmentTypeId: string,
        serviceRequestServiceTypeId: string,
        serviceRequestTosSystemId: string,
        serviceRequestSecondaryTosSystemId: string,
        customer: string,
        containUnitSpecification: boolean,
        additionalServices: any[],
        isAddsDeconCase: boolean,
        isAddsConteCase: boolean,
        showCargoMeasureProperties: boolean,
        showSaveAndLinkMOTButton: boolean,
        customsProperties: CustomsProperties,
        directVesselLoading?: boolean,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.xl
        });
        dialogRef.content.instance.serviceRequestId = serviceRequestId;
        dialogRef.content.instance.manipulation = manipulation;
        dialogRef.content.instance.shipmentTypeId = shipmentTypeId;
        dialogRef.content.instance.serviceRequestServiceTypeId = serviceRequestServiceTypeId;
        dialogRef.content.instance.serviceRequestTosSystemId = serviceRequestTosSystemId;
        dialogRef.content.instance.serviceRequestSecondaryTosSystemId = serviceRequestSecondaryTosSystemId;
        dialogRef.content.instance.customer = customer;
        dialogRef.content.instance.containUnitSpecification = containUnitSpecification;
        dialogRef.content.instance.additionalServices = additionalServices;
        dialogRef.content.instance.isAddsDeconCase = isAddsDeconCase;
        dialogRef.content.instance.isAddsConteCase = isAddsConteCase;
        dialogRef.content.instance.showCargoMeasureProperties = showCargoMeasureProperties;
        dialogRef.content.instance.showSaveAndLinkMOTButton = showSaveAndLinkMOTButton;
        dialogRef.content.instance.customsProperties = customsProperties;
        dialogRef.content.instance.directVesselLoading = directVesselLoading;
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return dialogRef.result;
    }

    public selectVesselModal(
        modalComponent: any,
        title: string,
        message: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.xl
        });
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return dialogRef.result;
    }

    public createShipmentPoolModal(
        modalComponent: any,
        title: string,
        message: string,
        serviceRequestId: number,
        manipulation: string,
        shipmentTypeId: string,
        serviceRequestTosSystemId: string,
        serviceRequestSecondaryTosSystemId: string,
        serviceRequestServiceTypeId: string,
        customer: string,
        mode: ViewMode,
        containUnitSpecification: boolean,
        additionalServices: any[],
        isAddsDeconCase: boolean,
        isAddsConteCase: boolean,
        showCargoMeasureProperties: boolean,
        showSaveAndLinkMOTButton: boolean,
        customsProperties: CustomsProperties,
        directVesselLoading?: boolean,
        shipmentPoolId?: number,
        serviceRequestVersionShipmentId?: number,
        serviceRequestVersion?: number,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        dialogRef.content.instance.serviceRequestId = serviceRequestId;
        dialogRef.content.instance.manipulation = manipulation;
        dialogRef.content.instance.shipmentTypeId = shipmentTypeId;
        dialogRef.content.instance.serviceRequestTosSystemId = serviceRequestTosSystemId;
        dialogRef.content.instance.serviceRequestSecondaryTosSystemId = serviceRequestSecondaryTosSystemId;
        dialogRef.content.instance.serviceRequestServiceTypeId = serviceRequestServiceTypeId;
        dialogRef.content.instance.customer = customer;
        dialogRef.content.instance.mode = mode;
        dialogRef.content.instance.containUnitSpecification = containUnitSpecification;
        dialogRef.content.instance.additionalServices = additionalServices;
        dialogRef.content.instance.isAddsDeconCase = isAddsDeconCase;
        dialogRef.content.instance.isAddsConteCase = isAddsConteCase;
        dialogRef.content.instance.showCargoMeasureProperties = showCargoMeasureProperties;
        dialogRef.content.instance.showSaveAndLinkMOTButton = showSaveAndLinkMOTButton;
        dialogRef.content.instance.customsProperties = customsProperties;
        dialogRef.content.instance.directVesselLoading = directVesselLoading;
        dialogRef.content.instance.shipmentPoolId = shipmentPoolId;
        dialogRef.content.instance.serviceRequestVersionShipmentId = serviceRequestVersionShipmentId;
        dialogRef.content.instance.serviceRequestVersion = serviceRequestVersion;
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return dialogRef.result;
    }

    public linkMotNonContainersModal(
        modalComponent: any,
        title: string,
        message: string,
        motItems: any[],
        motType: string,
        serviceRequestVersionShipmentId: any,
        serviceRequestId: number,
        serviceRequestTosSystemId: string,
        serviceRequestServiceTypeId: string,
        shipmentPoolId: number,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string,
        manipulation?: string,
        packageTypeCode?: string,
        poolWeightInVacuum?: number,
        poolUnloadingOrderedWeightInVacuum?: number
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.sm
        });
        dialogRef.content.instance.motItems = motItems;
        dialogRef.content.instance.motType = motType;
        dialogRef.content.instance.serviceRequestVersionShipmentId = serviceRequestVersionShipmentId;
        dialogRef.content.instance.serviceRequestId = serviceRequestId;
        dialogRef.content.instance.serviceRequestTosSystemId = serviceRequestTosSystemId;
        dialogRef.content.instance.serviceRequestServiceTypeId = serviceRequestServiceTypeId;
        dialogRef.content.instance.shipmentPoolId = shipmentPoolId;
        dialogRef.content.instance.manipulation = manipulation;
        dialogRef.content.instance.packageTypeCode = packageTypeCode;
        dialogRef.content.instance.poolWeightInVacuum = poolWeightInVacuum;
        dialogRef.content.instance.poolUnloadingOrderedWeightInVacuum = poolUnloadingOrderedWeightInVacuum;
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return dialogRef.result;
    }

    public linkItemsToMotsModal(
        modalComponent: any,
        title: string,
        message: string,
        isUnit: boolean,
        motItems: any[],
        items: ServiceRequestMotLinkItem[],
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        dialogRef.content.instance.isUnit = isUnit;
        dialogRef.content.instance.motItems = motItems;
        dialogRef.content.instance.items = items;
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return dialogRef.result;
    }

    public renameShipmentPoolModal(
        modalComponent: any,
        shipmentPoolId: number,
        shipmentPoolName: string,
        title: string,
        message: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        dialogRef.content.instance.shipmentPoolId = shipmentPoolId;
        dialogRef.content.instance.shipmentPoolName = shipmentPoolName;
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return dialogRef.result;
    }

    public openSelectedListWithMotsModal(
        modalComponent: any,
        title: string,
        message: string,
        selectedMotItemIds: any[],
        serviceRequestVersionShipmentId: number,
        serviceRequestId: number,
        serviceRequestTosSystemId: string,
        serviceRequestServiceTypeId: string,
        shipmentPoolId: number,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string,
        manipulation?: string,
        packageTypeCode?: string,
        poolWeightInVacuum?: number,
        poolUnloadingOrderedWeightInVacuum?: number
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: ModalWidth.xxl
        });
        dialogRef.content.instance.selectedMotItemIds = selectedMotItemIds;
        dialogRef.content.instance.serviceRequestVersionShipmentId = serviceRequestVersionShipmentId;
        dialogRef.content.instance.serviceRequestId = serviceRequestId;
        dialogRef.content.instance.serviceRequestTosSystemId = serviceRequestTosSystemId;
        dialogRef.content.instance.serviceRequestServiceTypeId = serviceRequestServiceTypeId;
        dialogRef.content.instance.shipmentPoolId = shipmentPoolId;
        dialogRef.content.instance.manipulation = manipulation;
        dialogRef.content.instance.packageTypeCode = packageTypeCode;
        dialogRef.content.instance.poolWeightInVacuum = poolWeightInVacuum;
        dialogRef.content.instance.poolUnloadingOrderedWeightInVacuum = poolUnloadingOrderedWeightInVacuum;
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return dialogRef.result;
    }

    public editLinkToMotDataModal(
        modalComponent: any,
        title: string,
        message: string,
        dataForEditing: any,
        serviceRequestId: number,
        serviceRequestTosSystemId: string,
        serviceRequestServiceTypeId: string,
        shipmentPoolId: number,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        dialogRef.content.instance.dataForEditing = dataForEditing;
        dialogRef.content.instance.serviceRequestId = serviceRequestId;
        dialogRef.content.instance.serviceRequestTosSystemId = serviceRequestTosSystemId;
        dialogRef.content.instance.serviceRequestServiceTypeId = serviceRequestServiceTypeId;
        dialogRef.content.instance.shipmentPoolId = shipmentPoolId;
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return dialogRef.result;
    }

    public editStorageDocumentItemModal(
        modalComponent: any,
        title: string,
        message: string,
        dataForEditing: any,
        storageDocumentId: number,
        storageDocumentTypeId: string,
        serviceRequestTosSystemId: string,
        mode: ViewMode,
        canSeeMotItems: boolean,
        canComplete: boolean,
        primaryStorageDocument: boolean,
        packageTypeCode: string,
        serviceTypeId: string,
        manipulationId: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        dialogRef.content.instance.dataForEditing = dataForEditing;
        dialogRef.content.instance.storageDocumentId = storageDocumentId;
        dialogRef.content.instance.storageDocumentTypeId = storageDocumentTypeId;
        dialogRef.content.instance.serviceRequestTosSystemId = serviceRequestTosSystemId;
        dialogRef.content.instance.mode = mode;
        dialogRef.content.instance.canSeeMotItems = canSeeMotItems;
        dialogRef.content.instance.canComplete = canComplete;
        dialogRef.content.instance.primaryStorageDocument = primaryStorageDocument;
        dialogRef.content.instance.packageTypeCode = packageTypeCode;
        dialogRef.content.instance.serviceTypeId = serviceTypeId;
        dialogRef.content.instance.manipulationId = manipulationId;
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return dialogRef.result;
    }

    public createStorageDocumentDIRM(
        modalComponent: any,
        title: string,
        message: string,
        serviceRequestVersionShipmentId: number,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        dialogRef.content.instance.serviceRequestVersionShipmentId = serviceRequestVersionShipmentId;
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return dialogRef.result;
    }

    public assignWarehouseModal(
        modalComponent: any,
        title: string,
        message: string,
        ids: number[],
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({ content: modalComponent, width: '40%' });
        dialogRef.content.instance.ids = ids;
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return dialogRef.result;
    }

    public enterScaleWeightModal(
        modalComponent: any,
        title: string,
        message: string,
        items: any[],
        serviceTypeId: string,
        manipulationId: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.xl
        });
        dialogRef.content.instance.items = items;
        dialogRef.content.instance.serviceTypeId = serviceTypeId;
        dialogRef.content.instance.manipulationId = manipulationId;
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return dialogRef.result;
    }

    public getContainersFromTOS(
        component: Type<AddContainersFromTosComponent>,
        options: {
            serviceRequestId?: number;
            cargoManifestId?: number;
            notificationTypeId?: string;
            manipulationId?: string;
            fromServiceRequest?: boolean;
            shipmentType?: string;
            tosVoyageNumber?: string;
            portVoyageNumber?: string;
            agentId?: number;
            customerId?: number;
            nominatedAgentShippingLineId?: number;
            isVesselAgent?: boolean;
            hasVesselContainerLineBypass?: boolean;
            matchingShippingLines?: any;
            manifestTypeId?: string;
            vesselName?: string;
            vesselCallSign?: string;
        }
    ): Observable<any> {
        const dialogRef = this.modalService.open({ content: component, width: ModalWidth.xxl });
        dialogRef.content.instance.yesButtonName = options.cargoManifestId
            ? this.translateService.instant(marker('Create Cargo List'))
            : this.translateService.instant(marker('Add items'));
        dialogRef.content.instance.cargoManifestId = options.cargoManifestId;
        dialogRef.content.instance.notificationTypeId = options.notificationTypeId;
        dialogRef.content.instance.manipulationId = options.manipulationId;
        dialogRef.content.instance.fromServiceRequest = options.fromServiceRequest;
        dialogRef.content.instance.shipmentType = options.shipmentType;
        dialogRef.content.instance.tosVoyageNumber = options.tosVoyageNumber;
        dialogRef.content.instance.portVoyageNumber = options.portVoyageNumber;
        dialogRef.content.instance.agentId = options.agentId;
        dialogRef.content.instance.customerId = options.customerId;
        dialogRef.content.instance.serviceRequestId = options.serviceRequestId;
        dialogRef.content.instance.nominatedAgentShippingLineId = options.nominatedAgentShippingLineId;
        dialogRef.content.instance.isVesselAgent = options.isVesselAgent;
        dialogRef.content.instance.hasVesselContainerLineBypass = options.hasVesselContainerLineBypass;
        dialogRef.content.instance.matchingShippingLines = options.matchingShippingLines;
        dialogRef.content.instance.manifestTypeId = options.manifestTypeId;
        dialogRef.content.instance.vesselName = options.vesselName;
        dialogRef.content.instance.vesselCallSign = options.vesselCallSign;
        return dialogRef.result;
    }

    public openXmlImportConfirmation(options: {
        xmlType: XmlTypes;
        isTargetEmpty: boolean;
        vesselMismatch: boolean;
    }): Observable<any> {
        const dialogRef = this.modalService.open({
            content: XmlImportConfirmationComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.md
        });
        dialogRef.content.instance.xmlType = options.xmlType;
        dialogRef.content.instance.isTargetEmpty = options.isTargetEmpty;
        dialogRef.content.instance.vesselMismatch = options.vesselMismatch;
        return dialogRef.result;
    }

    public openXmlImportReport(options: { title: string; summary?: any; errors: any[] }): Observable<any> {
        const dialogRef = this.modalService.open({
            content: XmlImportReportComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.xl
        });
        dialogRef.content.instance.title = options.title;
        dialogRef.content.instance.summary = options.summary;
        dialogRef.content.instance.errors = options.errors || [];
        return dialogRef.result;
    }

    public getContainersFromBulPCS(
        modalComponent: any,
        title: string,
        message: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string,
        cargoManifestId?: number,
        notificationTypeId?: string,
        manipulationId?: string,
        fromServiceRequest?: boolean,
        shipmentType?: string,
        tosVoyageNumber?: string,
        agentId?: number,
        customerId?: number,
        serviceRequestId?: number,
        additionalServices?: any[],
        shippingLineId?: number,
        releaseOrderTypeId?: string,
        releaseOrderId?: number,
        matchingShippingLines?: any,
        vesselVisitId?: number,
        voyageNumber?: string,
        departureVesselVisitId?: number,
        loadingVoyageNumber?: string,
        isVesselAgent?: boolean,
        hasVesselContainerLineBypass?: boolean,
        agentMatchingShippingLines?: number[]
    ): Observable<any> {
        const dialogRef = this.modalService.open({ content: modalComponent, width: ModalWidth.xxl });
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);
        dialogRef.content.instance.cargoManifestId = cargoManifestId;
        dialogRef.content.instance.notificationTypeId = notificationTypeId;
        dialogRef.content.instance.manipulationId = manipulationId;
        dialogRef.content.instance.fromServiceRequest = fromServiceRequest;
        dialogRef.content.instance.shipmentType = shipmentType;
        dialogRef.content.instance.tosVoyageNumber = tosVoyageNumber;
        dialogRef.content.instance.agentId = agentId;
        dialogRef.content.instance.customerId = customerId;
        dialogRef.content.instance.serviceRequestId = serviceRequestId;
        dialogRef.content.instance.additionalServices = additionalServices;
        dialogRef.content.instance.shippingLineId = shippingLineId;
        dialogRef.content.instance.releaseOrderTypeId = releaseOrderTypeId;
        dialogRef.content.instance.releaseOrderId = releaseOrderId;
        dialogRef.content.instance.matchingShippingLines = matchingShippingLines;
        dialogRef.content.instance.agentMatchingShippingLines = agentMatchingShippingLines;
        dialogRef.content.instance.vesselVisitId = vesselVisitId;
        dialogRef.content.instance.voyageNumber = voyageNumber;
        dialogRef.content.instance.departureVesselVisitId = departureVesselVisitId;
        dialogRef.content.instance.loadingVoyageNumber = loadingVoyageNumber;
        dialogRef.content.instance.isVesselAgent = isVesselAgent;
        dialogRef.content.instance.hasVesselContainerLineBypass = hasVesselContainerLineBypass;
        return dialogRef.result;
    }

    public getVehiclesFromBulPCS(
        modalComponent: any,
        title: string,
        message: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string,
        serviceRequestId?: number
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.xl
        });
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);
        dialogRef.content.instance.serviceRequestId = serviceRequestId;
        return dialogRef.result;
    }

    public getShipmentUnitsFromBulPCS(
        component: Type<AddShipmentUnitsFromBulPCSModalComponent>,
        title: string,
        serviceRequestId: number,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: component,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.xl
        });
        this.setCommonProperties(dialogRef, title, null, yesButtonName, noButtonName, cancelButtonName);
        dialogRef.content.instance.serviceRequestId = serviceRequestId;
        return dialogRef.result;
    }

    public getEmptyContainerForReleaseOrder(
        modalComponent: any,
        title: string,
        message: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string,
        releaseOrderId?: number
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.xl
        });
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);
        dialogRef.content.instance.releaseOrderId = releaseOrderId;
        return dialogRef.result;
    }

    public editServiceRequestAdditionalServiceModal(
        modalComponent: any,
        title: string,
        message: string,
        additionalService: any,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        dialogRef.content.instance.additionalService = additionalService;

        return dialogRef.result;
    }

    public createAmendmentDocumentModal(
        modalComponent: any,
        title: string,
        message: string,
        shipmentPoolItemId?: number,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string,
        nextButtonName?: string,
        saveButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);
        dialogRef.content.instance.shipmentPoolItemId = shipmentPoolItemId;
        dialogRef.content.instance.nextButtonName = nextButtonName;
        dialogRef.content.instance.saveButtonName = saveButtonName;
        return dialogRef.result;
    }

    public editAmendmentDocumentModal(
        modalComponent: any,
        title: string,
        message: string,
        id?: number,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string,
        saveButtonName?: string,
        isForSubmitOnly?: boolean
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);
        dialogRef.content.instance.id = id;
        dialogRef.content.instance.saveButtonName = saveButtonName;
        dialogRef.content.instance.isForSubmitOnly = isForSubmitOnly;
        return dialogRef.result;
    }

    public editServiceRequestAdditionalDataModal(
        modalComponent: any,
        title: string,
        message: string,
        serviceRequestId: number,
        manipulation: string,
        manipulationServiceRequestType: string,
        statusId: string,
        version: number,
        serviceRequestVersionAdditionalServices: any[],
        portId: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({ content: modalComponent, width: ModalWidth.xxl });
        dialogRef.content.instance.serviceRequestId = serviceRequestId;
        dialogRef.content.instance.manipulation = manipulation;
        dialogRef.content.instance.manipulationServiceRequestType = manipulationServiceRequestType;
        dialogRef.content.instance.statusId = statusId;
        dialogRef.content.instance.version = version;
        dialogRef.content.instance.serviceRequestVersionAdditionalServices = serviceRequestVersionAdditionalServices;
        dialogRef.content.instance.portId = portId;
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);
        return dialogRef.result;
    }

    public editServiceReuqestVersionContainerModal(
        modalComponent: any,
        title: string,
        message: string,
        dataForEditing: any,
        serviceRequestVersionId: number,
        unNamedContainers: boolean,
        fullOrEmptyId: string,
        mode: ViewMode,
        serviceRequestServiceType: string,
        matchingLines: any[],
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.xl
        });
        dialogRef.content.instance.dataForEditing = dataForEditing;
        dialogRef.content.instance.serviceRequestVersionId = serviceRequestVersionId;
        dialogRef.content.instance.fullOrEmptyId = fullOrEmptyId;
        dialogRef.content.instance.unNamedContainers = unNamedContainers;
        dialogRef.content.instance.mode = mode;
        dialogRef.content.instance.serviceRequestServiceType = serviceRequestServiceType;
        dialogRef.content.instance.matchingLines = matchingLines;
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return dialogRef.result;
    }

    public linkContainersModal(
        modalComponent: any,
        title: string,
        message: string,
        additionalServices: any[],
        serviceRequestVersionShipmentId: any,
        serviceRequestId: number,
        serviceRequestTosSystemId: string,
        serviceRequestServiceTypeId: string,
        shipmentTypeId: string,
        shipmentPoolId: number,
        isMotRequired: boolean,
        isAddsDeconCase: boolean,
        isAddsConteCase: boolean,
        manipulationId: string,
        motItems: any[],
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.md
        });
        dialogRef.content.instance.additionalServices = additionalServices;
        dialogRef.content.instance.serviceRequestVersionShipmentId = serviceRequestVersionShipmentId;
        dialogRef.content.instance.serviceRequestId = serviceRequestId;
        dialogRef.content.instance.serviceRequestTosSystemId = serviceRequestTosSystemId;
        dialogRef.content.instance.shipmentPoolId = shipmentPoolId;
        dialogRef.content.instance.serviceRequestServiceTypeId = serviceRequestServiceTypeId;
        dialogRef.content.instance.shipmentTypeId = shipmentTypeId;
        dialogRef.content.instance.isMotRequired = isMotRequired;
        dialogRef.content.instance.isAddsDeconCase = isAddsDeconCase;
        dialogRef.content.instance.isAddsConteCase = isAddsConteCase;
        dialogRef.content.instance.manipulationId = manipulationId;
        dialogRef.content.instance.motItems = motItems;
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return dialogRef.result;
    }

    public openSelectedListWithContainersModal(
        modalComponent: any,
        title: string,
        message: string,
        selectedContainerItemIds: any[],
        additionalServices: any[],
        serviceRequestVersionShipmentId: number,
        serviceRequestId: number,
        serviceRequestTosSystemId: string,
        serviceRequestServiceTypeId: string,
        shipmentTypeId: string,
        shipmentPoolId: number,
        isMotRequired: boolean,
        isAddsDeconCase: boolean,
        isAddsConteCase: boolean,
        manipulationId: string,
        motItems: any[],
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        dialogRef.content.instance.selectedContainerItemIds = selectedContainerItemIds;
        dialogRef.content.instance.additionalServices = additionalServices;
        dialogRef.content.instance.serviceRequestVersionShipmentId = serviceRequestVersionShipmentId;
        dialogRef.content.instance.serviceRequestId = serviceRequestId;
        dialogRef.content.instance.serviceRequestTosSystemId = serviceRequestTosSystemId;
        dialogRef.content.instance.serviceRequestServiceTypeId = serviceRequestServiceTypeId;
        dialogRef.content.instance.shipmentTypeId = shipmentTypeId;
        dialogRef.content.instance.shipmentPoolId = shipmentPoolId;
        dialogRef.content.instance.isMotRequired = isMotRequired;
        dialogRef.content.instance.isAddsDeconCase = isAddsDeconCase;
        dialogRef.content.instance.isAddsConteCase = isAddsConteCase;
        dialogRef.content.instance.manipulationId = manipulationId;
        dialogRef.content.instance.motItems = motItems;
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return dialogRef.result;
    }

    public editLinkToContainerDataModal(
        modalComponent: any,
        title: string,
        message: string,
        dataForEditing: any,
        additionalServices: any[],
        serviceRequestId: number,
        serviceRequestTosSystemId: string,
        serviceRequestServiceTypeId: string,
        shipmentTypeId: string,
        shipmentPoolId: number,
        isMotRequired: boolean,
        isAddsDeconCase: boolean,
        isAddsConteCase: boolean,
        manipulationId: string,
        motItems: any[],
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        dialogRef.content.instance.dataForEditing = dataForEditing;
        dialogRef.content.instance.additionalServices = additionalServices;
        dialogRef.content.instance.serviceRequestId = serviceRequestId;
        dialogRef.content.instance.serviceRequestTosSystemId = serviceRequestTosSystemId;
        dialogRef.content.instance.serviceRequestServiceTypeId = serviceRequestServiceTypeId;
        dialogRef.content.instance.shipmentTypeId = shipmentTypeId;
        dialogRef.content.instance.shipmentPoolId = shipmentPoolId;
        dialogRef.content.instance.isMotRequired = isMotRequired;
        dialogRef.content.instance.isAddsDeconCase = isAddsDeconCase;
        dialogRef.content.instance.isAddsConteCase = isAddsConteCase;
        dialogRef.content.instance.manipulationId = manipulationId;
        dialogRef.content.instance.motItems = motItems;
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return dialogRef.result;
    }

    public openSimpleDialog(
        modalComponent: any,
        title: string,
        message: string,
        width: ModalWidth,
        additionalData?: any,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({ content: modalComponent, width: width });
        dialogRef.content.instance.additionalData = additionalData;
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return dialogRef.result;
    }

    //TODO - use typed version of modal data where possible
    // Typed version of openSimpleDialog
    public openSimpleDialogWithData<T>(
        modalComponent: any,
        title: string,
        message: string,
        width: ModalWidth,
        additionalData?: T
    ): Observable<any> {
        const dialogRef = this.modalService.open({ content: modalComponent, width: width });
        dialogRef.content.instance.additionalData = additionalData;
        this.setCommonProperties(dialogRef, title, message);

        return dialogRef.result;
    }

    public openFileSelectorDialog(
        title: string,
        message: string,
        yesButtonName?: string,
        allowedExtensions?: string[]
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: FileUploadGenericModalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });

        dialogRef.content.instance.yesButtonName = yesButtonName;
        if (allowedExtensions)
            dialogRef.content.instance.attachmentFileUploadRestrictions = {
                maxFileSize: 5242880,
                allowedExtensions
            };
        this.setCommonProperties(dialogRef, title, message);

        return dialogRef.result;
    }

    private setCommonProperties(
        dialogRef: DialogRef,
        title?: string,
        message?: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): void {
        dialogRef.content.instance.title = title;
        dialogRef.content.instance.message = message;
        dialogRef.content.instance.yesButtonName = yesButtonName;
        dialogRef.content.instance.noButtonName = noButtonName;
        dialogRef.content.instance.cancelButtonName = cancelButtonName;
    }

    public openDeliveryOrderCreateModal(
        vesselVisitId?,
        terminalId?,
        portId?,
        vesselMaster?,
        berthId?
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: EditDeliveryOrderComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        dialogRef.content.instance.vesselVisitId = vesselVisitId;
        dialogRef.content.instance.terminalId = terminalId;
        dialogRef.content.instance.portId = portId;
        dialogRef.content.instance.vesselMaster = vesselMaster;
        dialogRef.content.instance.berthId = berthId;
        this.setCommonProperties(
            dialogRef,
            this.translateService.instant(marker('Add Delivery Order')),
            '',
            this.translateService.instant(marker('Add')),
            null,
            this.translateService.instant(marker('Cancel'))
        );

        return dialogRef.result;
    }

    public addDeliveryOrderItems(deliveryOrderId, deliveryOrderItemId?: number): Observable<any> {
        const dialogRef = this.modalService.open({
            content: AddDeliveryOrderItemComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        dialogRef.content.instance.deliveryOrderId = deliveryOrderId;
        dialogRef.content.instance.deliveryOrderItemId = deliveryOrderItemId;
        this.setCommonProperties(
            dialogRef,
            this.translateService.instant(marker('Add Delivery Order')),
            '',
            this.translateService.instant(marker('Add')),
            null,
            this.translateService.instant(marker('Cancel'))
        );
        return dialogRef.result;
    }

    public showErrorDialog(error: any, validationErrors?: any[]) {
        const valError = error?.httpResponse?.data || error?.error || error;
        const dialogRef = this.modalService.open({
            content: ErrorDialogComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        dialogRef.content.instance.validationErrors = validationErrors;
        dialogRef.content.instance.setMessage(valError.errorMessage, valError.exception, valError.errors);
        dialogRef.content.instance.setStackTrace(valError?.stackTrace);
    }

    public addVessel(vesselId?: number): Observable<any> {
        const dialogRef = this.modalService.open({
            content: AddVesselComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.sm
        });
        dialogRef.content.instance.vesselId = vesselId;
        this.setCommonProperties(
            dialogRef,
            this.translateService.instant(marker('Temporary New Vessel')),
            '',
            this.translateService.instant(marker('Create')),
            null,
            this.translateService.instant(marker('Cancel'))
        );
        return dialogRef.result;
    }

    public openAuditModal(auditLog: any) {
        const dialogRef = this.modalService.open({
            content: AuditLogModalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        dialogRef.content.instance.auditLog = auditLog;
        this.setCommonProperties(dialogRef, this.translateService.instant(marker('Audit Log Preview')), '');
        return dialogRef.result;
    }

    public openCustomsDeclarationModal() {
        const dialogRef = this.modalService.open({
            content: CustomsDeclarationModalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        this.setCommonProperties(dialogRef, this.translateService.instant(marker('Customs Call')), '');
        return dialogRef.result;
    }

    private normalizeValidationRows(rows?: ValidationOutcomeRowLike[]): ValidationOutcomeRow[] {
        return (rows || []).map((row) => ({
            firstValue: this.toValidationText(row.firstValue, row.identificator, row.id),
            secondValue: this.toValidationText(row.secondValue, row.message, row.name),
            severity: row.severity
        }));
    }

    private toValidationText(...values: Array<string | number | undefined>): string {
        const value = values.find((item) => item !== null && item !== undefined && item !== '');

        return value === null || value === undefined ? '' : String(value);
    }

    public openMessage(
        messageId: number,
        messageNumber: string,
        messageData: string,
        errorMessage: string,
        resendButtonVisible: boolean = true
    ) {
        const dialogRef = this.modalService.open({
            content: MessageModalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.xl
        });
        dialogRef.content.instance.messageId = messageId;
        dialogRef.content.instance.messageNumber = messageNumber;
        dialogRef.content.instance.messageData = messageData;
        dialogRef.content.instance.errorMessage = errorMessage;
        dialogRef.content.instance.resendButtonVisible = resendButtonVisible;
        this.setCommonProperties(dialogRef, this.translateService.instant(marker('Message')), '');
        return dialogRef.result;
    }

    public addQuestion(question?: any): Observable<any> {
        const dialogRef = this.modalService.open({
            content: AddFaqModalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        dialogRef.content.instance.question = question;
        this.setCommonProperties(
            dialogRef,
            this.translateService.instant(marker('FAQ')),
            '',
            this.translateService.instant(marker('Add')),
            null,
            this.translateService.instant(marker('Cancel'))
        );
        return dialogRef.result;
    }

    public addDocument(document?: any): Observable<any> {
        const dialogRef = this.modalService.open({
            content: AddDocumentModalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        dialogRef.content.instance.document = document;
        this.setCommonProperties(
            dialogRef,
            this.translateService.instant(marker('User Manual')),
            '',
            this.translateService.instant(marker('Add')),
            null,
            this.translateService.instant(marker('Cancel'))
        );
        return dialogRef.result;
    }

    public saveVehicle(vesselVisitId: number, vehicle?: any): Observable<any> {
        const dialogRef = this.modalService.open({
            content: SaveVehicleModalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        dialogRef.content.instance.vesselVisitId = vesselVisitId;
        dialogRef.content.instance.vehicle = vehicle;

        return dialogRef.result;
    }

    public openNotificationsGridModal(component: Type<TabNotificationsComponent>): Observable<any> {
        const dialogRef = this.modalService.open({
            content: component,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.xl
        });
        return dialogRef.result;
    }

    public openSaveGeneralActModal(
        vesselVisitId: number,
        generalAct?: GeneralActDisplayModel,
        agentId?: string,
        vesselName?: string,
        vesselVisitEta?: Date
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: SaveGeneralActModalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        dialogRef.content.instance.vesselVisitId = vesselVisitId;
        dialogRef.content.instance.agentId = agentId;
        dialogRef.content.instance.vesselName = vesselName;
        dialogRef.content.instance.vesselVisitEta = vesselVisitEta;
        dialogRef.content.instance.generalAct = generalAct;
        return dialogRef.result;
    }

    public openGeneralActManualCorrectionModal(
        id: number,
        cargoTypeId: string,
        cargoDirectionId: string,
        approvedCustomsApprovalOverrideConfirmed: boolean = false
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: GeneralActManualCorrectionModalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        dialogRef.content.instance.id = id;
        dialogRef.content.instance.cargoTypeId = cargoTypeId;
        dialogRef.content.instance.cargoDirectionId = cargoDirectionId;
        dialogRef.content.instance.approvedCustomsApprovalOverrideConfirmed = approvedCustomsApprovalOverrideConfirmed;
        return dialogRef.result;
    }

    public openCreateReleaseOrder(
        modalComponent: any,
        title: string,
        message: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.md
        });
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);
        return dialogRef.result;
    }

    public openEditReleaseOrder(
        modalComponent: any,
        title: string,
        message: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string,
        model?: EditReleaseOrderModalModel
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.md
        });
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);
        dialogRef.content.instance.model = model;
        return dialogRef.result;
    }

    public confirmVehicleVisitMoves(
        modalComponent: any,
        title: string,
        message: string,
        vehicleItemIds: any[],
        serviceRequestId: number,
        manipulation: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.sm
        });
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);
        dialogRef.content.instance.vehicleItemIds = vehicleItemIds;
        dialogRef.content.instance.serviceRequestId = serviceRequestId;
        dialogRef.content.instance.manipulation = manipulation;
        return dialogRef.result;
    }

    public cancelVehicleVisitMoves(
        modalComponent: any,
        title: string,
        message: string,
        vehicleItemIds: any[],
        serviceRequestId: number,
        manipulation: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.sm
        });
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);
        dialogRef.content.instance.vehicleItemIds = vehicleItemIds;
        dialogRef.content.instance.serviceRequestId = serviceRequestId;
        dialogRef.content.instance.manipulation = manipulation;
        return dialogRef.result;
    }

    public deleteVehicleVisitMoves(
        modalComponent: any,
        title: string,
        message: string,
        vehicleItemIds: any[],
        serviceRequestId: number,
        manipulation: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.sm
        });
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);
        dialogRef.content.instance.vehicleItemIds = vehicleItemIds;
        dialogRef.content.instance.serviceRequestId = serviceRequestId;
        dialogRef.content.instance.manipulation = manipulation;
        return dialogRef.result;
    }

    public openEditServiceRequestVersionVehicle(
        modalComponent: any,
        title: string,
        message: string,
        id?: number,
        serviceRequestId?: number,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.xl
        });
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);
        dialogRef.content.instance.id = id;
        dialogRef.content.instance.serviceRequestId = serviceRequestId;
        return dialogRef.result;
    }

    public openEditServiceRequestPassengerVehicle(
        modalComponent: any,
        title: string,
        message: string,
        id?: number,
        serviceRequestId?: number,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.xl
        });
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);
        dialogRef.content.instance.id = id;
        dialogRef.content.instance.serviceRequestId = serviceRequestId;
        return dialogRef.result;
    }

    public openServiceRequestCargoUnit(
        modalComponent: any,
        title: string,
        message: string,
        serviceRequestId: number,
        options: any,
        dataForEditing?: any,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string,
        hideIdentificationNumber = false,
        isIdentificationNumberRequired = false,
        showMotNumber = false
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.xl
        });
        dialogRef.content.instance.serviceRequestId = serviceRequestId;
        dialogRef.content.instance.options = options;
        dialogRef.content.instance.dataForEditing = dataForEditing;
        dialogRef.content.instance.hideIdentificationNumber = hideIdentificationNumber;
        dialogRef.content.instance.isIdentificationNumberRequired = isIdentificationNumberRequired;
        dialogRef.content.instance.showMotNumber = showMotNumber;
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);
        return dialogRef.result;
    }

    public openAddUnnamedContainer(
        modalComponent: any,
        title: string,
        message: string,
        serviceRequestId: number,
        agentId: number,
        matchingLines: any[],
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);
        dialogRef.content.instance.serviceRequestId = serviceRequestId;
        dialogRef.content.instance.agentId = agentId;
        dialogRef.content.instance.matchingLines = matchingLines;
        return dialogRef.result;
    }

    public openCreateServiceRequestFromCargoManifestModal(
        containers: boolean,
        roro: boolean,
        systemId: string,
        vesselVisitId: number,
        terminalId: string,
        terminalOperatorId: number,
        portId: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: CreateServiceRequestFromCargoManifestModalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        dialogRef.content.instance.containers = containers;
        dialogRef.content.instance.roro = roro;
        dialogRef.content.instance.systemId = systemId;
        dialogRef.content.instance.vesselVisitId = vesselVisitId;
        dialogRef.content.instance.terminalId = terminalId;
        dialogRef.content.instance.terminalOperatorId = terminalOperatorId;
        dialogRef.content.instance.portId = portId;
        return dialogRef.result;
    }

    public openDuplicateCmiWarningModal(duplicates: any[]): Observable<any> {
        const dialogRef = this.modalService.open({
            content: DuplicateCmiWarningModalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        dialogRef.content.instance.duplicates = duplicates;
        return dialogRef.result;
    }

    public openDocumentsForSrModal(
        component: Type<DocumentsForServiceRequestModalComponent>,
        params: {
            serviceRequestId: number;
            shipmentId?: number;
            shipmentDetailId?: number;
        }
    ): void {
        const dialogRef = this.modalService.open({
            content: component,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.xl
        });

        const instance = dialogRef.content.instance;

        instance.serviceRequestId = params.serviceRequestId;
        instance.shipmentId = params.shipmentId;
        instance.shipmentDetailId = params.shipmentDetailId;
        instance.title = this.translateService.instant(marker('Documents for Service Request'));
    }

    public openManifestEditVehicle(title: string, id?: number, cargoManifestId?: number): Observable<any> {
        const dialogRef = this.modalService.open({
            content: ManifestVehicleEditModalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.xl
        });
        dialogRef.content.instance.title = title;
        dialogRef.content.instance.id = id;
        dialogRef.content.instance.cargoManifestId = cargoManifestId;
        return dialogRef.result;
    }

    public openDraftSurveyModal(
        modalComponent: any,
        dataForEditing: any,
        mode: string,
        draftSurveyId: number,
        vesselVisitId: number,
        title: string,
        message: string,
        yesButtonName?: string,
        noButtonName?: string,
        cancelButtonName?: string
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: modalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });
        dialogRef.content.instance.dataForEditing = dataForEditing;
        dialogRef.content.instance.mode = mode;
        dialogRef.content.instance.draftSurveyId = draftSurveyId;
        dialogRef.content.instance.vesselVisitId = vesselVisitId;
        this.setCommonProperties(dialogRef, title, message, yesButtonName, noButtonName, cancelButtonName);

        return dialogRef.result;
    }

    public openAttachmentList(options: {
        pcsAttachments?: any[];
        notificationAttachments?: any[];
        draftSurveyAttachments?: any[];
        customsHoldAttachments?: any[];
        faqDocumentAttachments?: any[];
        pcsDocumentAndApprovalStatusId?: string;
        openedAttachmentId?: number;
    }): Observable<any> {
        const dialogRef = this.modalService.open({
            content: AttachmentListDialogComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.lg
        });

        const instance = dialogRef.content.instance as any;
        if (options?.pcsAttachments) instance.pcsAttachments = options.pcsAttachments;
        if (options?.notificationAttachments) instance.notificationAttachments = options.notificationAttachments;
        if (options?.draftSurveyAttachments) instance.draftSurveyAttachments = options.draftSurveyAttachments;
        if (options?.customsHoldAttachments) instance.customsHoldAttachments = options.customsHoldAttachments;
        if (options?.faqDocumentAttachments) instance.faqDocumentAttachments = options.faqDocumentAttachments;
        if (options?.openedAttachmentId) instance.openedAttachmentId = options.openedAttachmentId;
        if (options?.pcsDocumentAndApprovalStatusId)
            instance.pcsDocumentAndApprovalStatusId = options.pcsDocumentAndApprovalStatusId;

        return dialogRef.result;
    }

    public openDeliveryOrderItemQuantitiesModal(
        component: Type<DeliveryOrderItemQuantitiesModalComponent>,
        itemIds: number[]
    ): Observable<any> {
        const dialogRef = this.modalService.open({
            content: component,
            width: ModalWidth.xxl
        });
        dialogRef.content.instance.itemIds = itemIds;
        return dialogRef.result;
    }

    public completeOrCancel(options: {
        action: 'Complete' | 'Cancel' | 'Confirm';
        title: string;
        message: string;
        showDate?: boolean;
        showRemark?: boolean;
        requireRemark?: boolean;
        dateLabel?: string;
        remarkLabel?: string;
        initialDate?: Date;
    }): Observable<any> {
        const {
            action,
            title,
            message,
            showDate = false,
            showRemark = false,
            requireRemark = false,
            dateLabel,
            remarkLabel,
            initialDate
        } = options;
        const dialogRef = this.modalService.open({
            content: CompleteCancelModalComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.md
        });
        dialogRef.content.instance.action = action;
        dialogRef.content.instance.showDate = showDate;
        dialogRef.content.instance.showRemark = showRemark;
        dialogRef.content.instance.requireRemark = requireRemark;
        dialogRef.content.instance.dateLabel = dateLabel;
        dialogRef.content.instance.remarkLabel = remarkLabel;
        dialogRef.content.instance.initialDate = initialDate;
        const primaryButtonText = this.translateService.instant(marker(action));

        this.setCommonProperties(
            dialogRef,
            title,
            message,
            primaryButtonText,
            null,
            this.translateService.instant(marker('Cancel'))
        );

        return dialogRef.result;
    }
}
